<template>
    <div class="lms__user_account_security_mfa_form">
        <template v-if="!activeForm && !userHasMfaEnabled">
            <component-button
                :size="'full'"
                :theme="'default'"
                :name="'lms-user-account-security-mfa-form-enable'"
                :disabled="user.isInpersonated()"
                @click="switchActiveForm"
            >{{ $t('auth.twoFactorAuth.buttons.enableTwoFactorAuth') }}
            </component-button>
        </template>
        <template v-else>
            <div class="lms__user_account_security_mfa_form_box">
                <template v-if="userHasMfaEnabled">
                    <template v-if="userHasMfaRequired">
                        <component-note class="lms__user_account_security_mfa_form_box_error">{{ $t('auth.twoFactorAuth.text.youCant') }}</component-note>
                    </template>
                    <component-button
                        :theme="'default'"
                        :disabled="userHasMfaRequired || user.isInpersonated()"
                        :size="'full'"
                        :name="'lms-user-account-security-mfa-form-open'"
                        @click="openNotifyDisableMfa"
                    >{{ $t('auth.twoFactorAuth.buttons.disableTwoFactorAuth') }}
                    </component-button>
                    <pop-notify
                        ref="popnotifydisablemfa"
                        :class="'pop__notify--disablemfa'"
                        :title-text="$t('auth.twoFactorAuth.notify.titleDisable')"
                        :content-text="$t('auth.twoFactorAuth.notify.textDoYouWant')"
                        :cancel-button-text="$t('auth.twoFactorAuth.notify.dismiss')"
                        :submit-button-danger="true"
                        :submit-button-text="$t('auth.twoFactorAuth.notify.confirm')"
                        :submit-button-animate-loading="true"
                        :submit-button-animate-loading-name="'mfadisable'"
                        @emit-submit="disableMfa"
                    />
                </template>
                <template v-else>
                    <component-text>{{ $t('auth.twoFactorAuth.text.scanThisCode') }}</component-text>
                    <component-qrcode
                        :secret="mfaSecret"
                        :label="QRCodeLabel"
                        :issuer="QRCodeIssuer"
                    />
                    <component-text>{{ $t('auth.twoFactorAuth.text.enterThe6Digit') }}</component-text>
                    <form-text
                        class="lms__user_account_security_mfa_form_box_code"
                        :v-validate="{ required: true, regex: /^[0-9]*$/, length: 6 }"
                        :label="$t('auth.twoFactorAuth.labels.verificationCoda')"
                        :name="$t('forms.dataVvAs.code')"
                        :data-vv-as="$t('forms.dataVvAs.code')"
                        v-model="mfaCode"
                    />
                    <component-note
                        v-if="mfaError"
                        class="lms__user_account_security_mfa_form_box_error"
                        :danger="true"
                    >{{ mfaError }}
                    </component-note>
                    <div class="columns">
                        <div class="column"></div>
                        <div class="column is-narrow">
                            <component-button
                                :theme="'default'"
                                :size="'full'"
                                :animate-loading="true"
                                @click="switchActiveForm"
                                :name="'lms-user-account-security-mfa-form-cancel'"
                            >{{ $t('auth.twoFactorAuth.buttons.cancel') }}
                            </component-button>
                        </div>
                        <div class="column is-narrow">
                            <component-button
                                :size="'full'"
                                :animate-loading="true"
                                @click="submitMfa"
                                :name="'lms-user-account-security-mfa-form-verify'"
                            >{{ $t('auth.twoFactorAuth.buttons.verifyAndUpdate') }}
                            </component-button>
                        </div>
                    </div>
                </template>
            </div>
        </template>
    </div>
</template>

<script>
import ComponentQrcode from '@/components/default/shared/ComponentQRCode'
import ComponentNote from '@/components/default/shared/ComponentNote'
import ComponentButton from '@/components/default/shared/ComponentButton'
import FormText from '@/components/default/forms/FormText'
import ComponentText from '@/components/default/shared/ComponentText'
import PopNotify from '@/components/default/shared/PopNotify'
import { User } from '@/User'

export default {
  name: 'lms-user-account-security-mfa-form',
  components: { PopNotify, ComponentText, FormText, ComponentButton, ComponentNote, ComponentQrcode },
  data () {
    return {
      activeForm: false,
      user: new User()
    }
  },
  computed: {
    mfaSecret () {
      return this.$store.getters.getAuthUserMfaSecret
    },
    mfaCode: {
      get () {
        return this.$store.getters.getAuthUserMfaCode
      },
      set (value) {
        this.$store.dispatch('authUserSetMfaCode', value)
      }
    },
    mfaError () {
      return this.$store.getters.getAuthUserMfaError
    },
    QRCodeIssuer () {
      return 'Me Learning LMS'
    },
    QRCodeLabel () {
      return this.$store.getters.getAuthUserFirstStageTokenDataEmail
    },
    userHasMfaRequired () {
      return this.$store.getters.getAuthUserHasMfaRequired
    },
    userHasMfaEnabled () {
      return this.$store.getters.getAuthUserHasMfaEnabled
    }
  },
  watch: {
    userHasMfaEnabled (value) {
      if (!value) {
        this.generateQRCode()
      }
    }
  },
  methods: {
    switchActiveForm () {
      this.activeForm = !this.activeForm
      if (this.activeForm && !this.userHasMfaEnabled) {
        this.generateQRCode()
      }
    },
    generateQRCode () {
      this.$store.dispatch('authAdminGetMFASecretCode')
    },
    submitMfa () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$store.dispatch('authUserMfaVerifySoftwareToken').then(() => {
            this.$store.dispatch('authUserRefreshFirstStageToken').finally(() => {
              this.$eventBus.$emit('stop-sending')
              this.switchActiveForm()
            })
          }).catch(() => {
            this.$eventBus.$emit('stop-sending')
          })
        }
      })
    },
    openNotifyDisableMfa () {
      this.$refs.popnotifydisablemfa.openNotify()
    },
    disableMfa () {
      this.$store.dispatch('authAdminMfaDisable').finally(() => {
        this.$eventBus.$emit('stop-disable-mfa')
      })
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/lms/user/account/_lmsuseraccountsecuritymfaform.scss";
</style>
